import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthenticatedApi } from './useAuthenticatedApi';

interface RestoreSummary {
  total_count: number;
  successful_count: number;
  failed_count: number;
  in_progress_count: number;
  last_restore: Restore | null;
}

interface Snapshot {
  id: string;
  workspace_id: string;
  tags: string[];
  description?: string | null;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETE' | 'FAILED';
  snapshot_size_bytes: number | null;
  source_compressed_total_size_bytes: number | null;
  source_uncompressed_total_size_bytes: number | null;
  source_collection_count: number | null;
  source_index_count: number | null;
  source_document_count: number | null;
  error: string | null;  // Generic error message (internal details removed for security)
  created_at: string;
  updated_at: string;
  started_at: string | null;
  completed_at: string | null;
  restore_summary?: RestoreSummary | null;
}

interface Restore {
  id: string;
  snapshot_id: string;
  workspace_id: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETE' | 'FAILED';
  error: string | null;
  created_at: string;
  updated_at: string;
  started_at: string | null;
  completed_at: string | null;
}

interface CreateSnapshotResponse {
  snapshot: Snapshot | null;
  task_id: string;
}

interface RestoreSnapshotResponse {
  restore: Restore | null;
  task_id: string;
}

interface CooldownStatus {
  can_create_snapshot: boolean;
  cooldown_expires_at: string | null;
  minutes_remaining: number | null;
}

interface DeleteSnapshotResponse {
  success: boolean;
  message: string;
}

interface PaginatedSnapshotsResponse {
  snapshots: Snapshot[];
  total: number;
  skip: number;
  limit: number;
}

interface UseSnapshotsReturn {
  snapshots: Snapshot[];
  restores: Restore[];
  loading: boolean;
  restoresLoading: boolean;
  error: string | null;
  restoresError: string | null;
  creatingSnapshot: boolean;
  cooldownStatus: CooldownStatus | null;
  snapshotTotal: number;
  hasMore: boolean;
  loadingMore: boolean;
  fetchSnapshots: (includeRestoreSummary?: boolean, reset?: boolean, isBackgroundPoll?: boolean) => Promise<void>;
  loadMoreSnapshots: (includeRestoreSummary?: boolean) => Promise<void>;
  createSnapshot: (tags?: string[], description?: string) => Promise<CreateSnapshotResponse>;
  updateSnapshotMetadata: (snapshotId: string, tags: string[] | null, description?: string | null) => Promise<Snapshot>;
  deleteSnapshot: (snapshotId: string) => Promise<DeleteSnapshotResponse>;
  restoreSnapshot: (snapshotId: string) => Promise<RestoreSnapshotResponse>;
  fetchRestores: (snapshotId: string) => Promise<Restore[]>;
  fetchAllRestores: (snapshotId?: string) => Promise<void>;
  fetchActiveRestores: () => Promise<void>;
  getRestoreDetails: (restoreId: string) => Promise<Restore>;
  getSnapshotById: (snapshotId: string) => Promise<Snapshot>;
  checkCooldown: () => Promise<void>;
}

export const useSnapshots = (workspaceId: string | undefined, enabled: boolean = true): UseSnapshotsReturn => {
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [restores, setRestores] = useState<Restore[]>([]);
  const [loading, setLoading] = useState(true);
  const [restoresLoading, setRestoresLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [restoresError, setRestoresError] = useState<string | null>(null);
  const [creatingSnapshot, setCreatingSnapshot] = useState(false);
  const [cooldownStatus, setCooldownStatus] = useState<CooldownStatus | null>(null);

  // Pagination state
  const [snapshotPageSize] = useState(5);
  const [snapshotTotal, setSnapshotTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  const { get, post, patch, delete: deleteRequest } = useAuthenticatedApi();

  // Track polling intervals for active operations
  const pollingIntervalRef = useRef<number | null>(null);
  const cooldownPollingIntervalRef = useRef<number | null>(null);

  // Calculate if there are more snapshots to load
  const hasMore = snapshots.length < snapshotTotal;

  const fetchSnapshots = useCallback(async (includeRestoreSummary: boolean = true, reset: boolean = true, isBackgroundPoll: boolean = false) => {
    if (!workspaceId) return;

    try {
      // Don't show loading indicator for background polls to avoid UI flicker
      if (!isBackgroundPoll) {
        setLoading(true);
      }
      setError(null);

      // When background polling, fetch all currently loaded snapshots to preserve pagination state
      // When resetting (initial load or refresh), fetch only the first page
      const limit = isBackgroundPoll && snapshots.length > 0
        ? snapshots.length
        : snapshotPageSize;

      const url = `/workspaces/${workspaceId}/database/snapshots?include_restore_summary=${includeRestoreSummary}&skip=0&limit=${limit}`;

      const response: PaginatedSnapshotsResponse = await get(url);

      // Replace snapshots (reset)
      setSnapshots(response.snapshots);
      setSnapshotTotal(response.total);
    } catch (err) {
      setError('Failed to load snapshots');
    } finally {
      if (!isBackgroundPoll) {
        setLoading(false);
      }
    }
  }, [workspaceId, get, snapshotPageSize, snapshots.length]);

  const loadMoreSnapshots = useCallback(async (includeRestoreSummary: boolean = true) => {
    if (!workspaceId || loadingMore || !hasMore) return;

    try {
      setLoadingMore(true);
      setError(null);

      // Fetch next batch starting from current length
      const skip = snapshots.length;
      const url = `/workspaces/${workspaceId}/database/snapshots?include_restore_summary=${includeRestoreSummary}&skip=${skip}&limit=${snapshotPageSize}`;

      const response: PaginatedSnapshotsResponse = await get(url);

      // Append new snapshots to existing ones
      setSnapshots(prev => [...prev, ...response.snapshots]);
      setSnapshotTotal(response.total);
    } catch (err) {
      setError('Failed to load more snapshots');
    } finally {
      setLoadingMore(false);
    }
  }, [workspaceId, get, snapshotPageSize, snapshots.length, loadingMore, hasMore]);

  const checkCooldown = useCallback(async () => {
    if (!workspaceId) return;

    try {
      const response = await get(`/workspaces/${workspaceId}/database/snapshots/cooldown`);
      setCooldownStatus(response);
    } catch (err) {
      // Don't set error state for cooldown checks - not critical
    }
  }, [workspaceId, get]);

  const createSnapshot = useCallback(async (tags?: string[], description?: string): Promise<CreateSnapshotResponse> => {
    if (!workspaceId) {
      throw new Error('Workspace ID is required');
    }

    setCreatingSnapshot(true);
    try {
      const response = await post(`/workspaces/${workspaceId}/database/snapshots`, { 
        tags: tags || null,
        description: description || null
      });

      // Refresh snapshots list and cooldown status
      await Promise.all([fetchSnapshots(), checkCooldown()]);

      return response;
    } catch (err: any) {
      // ApiError has message and data properties
      const errorMessage = err.message || err.data?.detail || 'Failed to create snapshot';
      throw new Error(errorMessage);
    } finally {
      setCreatingSnapshot(false);
    }
  }, [workspaceId, post, fetchSnapshots, checkCooldown]);

  const updateSnapshotMetadata = useCallback(async (snapshotId: string, tags: string[] | null, description?: string | null): Promise<Snapshot> => {
    if (!workspaceId) {
      throw new Error('Workspace ID is required');
    }

    try {
      const response = await patch(
        `/workspaces/${workspaceId}/database/snapshots/${snapshotId}`,
        {
          tags: tags || null,
          description: description !== undefined ? description : null
        }
      );

      // Update local state
      setSnapshots(prevSnapshots =>
        prevSnapshots.map(s => s.id === snapshotId ? response : s)
      );

      return response;
    } catch (err: any) {
      // ApiError has message and data properties
      const errorMessage = err.message || err.data?.detail || 'Failed to update snapshot metadata';
      throw new Error(errorMessage);
    }
  }, [workspaceId, patch]);

  const deleteSnapshot = useCallback(async (snapshotId: string): Promise<DeleteSnapshotResponse> => {
    if (!workspaceId) {
      throw new Error('Workspace ID is required');
    }

    try {
      const response = await deleteRequest(`/workspaces/${workspaceId}/database/snapshots/${snapshotId}`);

      // Remove snapshot from local state
      setSnapshots(prevSnapshots =>
        prevSnapshots.filter(s => s.id !== snapshotId)
      );

      // Remove associated restores from local state
      setRestores(prevRestores =>
        prevRestores.filter(r => r.snapshot_id !== snapshotId)
      );

      return response;
    } catch (err: any) {
      // ApiError has message and data properties
      const errorMessage = err.message || err.data?.detail || 'Failed to delete snapshot';
      throw new Error(errorMessage);
    }
  }, [workspaceId, deleteRequest]);

  const fetchRestores = useCallback(async (snapshotId: string): Promise<Restore[]> => {
    if (!workspaceId) {
      return [];
    }

    try {
      const response = await get(
        `/workspaces/${workspaceId}/database/snapshots/${snapshotId}/restores`
      );
      return response;
    } catch (err) {
      throw new Error('Failed to fetch restores');
    }
  }, [workspaceId, get]);

  const fetchAllRestores = useCallback(async (snapshotId?: string) => {
    if (!workspaceId) return;

    try {
      setRestoresLoading(true);
      setRestoresError(null);

      // Build URL with optional snapshot_id filter
      const url = snapshotId
        ? `/workspaces/${workspaceId}/database/snapshots/restores?snapshot_id=${snapshotId}`
        : `/workspaces/${workspaceId}/database/snapshots/restores`;

      const response = await get(url);
      setRestores(response);
    } catch (err) {
      setRestoresError('Failed to load restore operations');
    } finally {
      setRestoresLoading(false);
    }
  }, [workspaceId, get]);

  // Fetch only active restores (PENDING or IN_PROGRESS)
  const fetchActiveRestores = useCallback(async () => {
    if (!workspaceId) return;

    try {
      const response = await get(
        `/workspaces/${workspaceId}/database/snapshots/restores?status=PENDING&status=IN_PROGRESS`
      );
      setRestores(response);
    } catch (err) {
      setRestoresError('Failed to load active restore operations');
    }
  }, [workspaceId, get]);

  const restoreSnapshot = useCallback(async (
    snapshotId: string
  ): Promise<RestoreSnapshotResponse> => {
    if (!workspaceId) {
      throw new Error('Workspace ID is required');
    }

    try {
      const response = await post(
        `/workspaces/${workspaceId}/database/snapshots/${snapshotId}/restore`,
        {} // No body needed - workspace database is used automatically
      );

      // Immediately fetch all restores to include the new one
      await fetchAllRestores();

      return response;
    } catch (err: any) {
      // ApiError has message and data properties
      const errorMessage = err.message || err.data?.detail || 'Failed to restore snapshot';
      throw new Error(errorMessage);
    }
  }, [workspaceId, post, fetchAllRestores]);

  const getRestoreDetails = useCallback(async (restoreId: string): Promise<Restore> => {
    if (!workspaceId) {
      throw new Error('Workspace ID is required');
    }

    try {
      const response = await get(
        `/workspaces/${workspaceId}/database/snapshots/restores/${restoreId}`
      );

      // Update the restore in local state
      setRestores(prevRestores =>
        prevRestores.map(r => r.id === restoreId ? response : r)
      );

      return response;
    } catch (err: any) {
      // ApiError has message and data properties
      const errorMessage = err.message || err.data?.detail || 'Failed to fetch restore details';
      throw new Error(errorMessage);
    }
  }, [workspaceId, get]);

  const getSnapshotById = useCallback(async (snapshotId: string): Promise<Snapshot> => {
    if (!workspaceId) {
      throw new Error('Workspace ID is required');
    }

    try {
      const response = await get(
        `/workspaces/${workspaceId}/database/snapshots/${snapshotId}?include_restore_summary=true`
      );

      // Update the snapshot in local state if it exists
      setSnapshots(prevSnapshots => {
        const existingIndex = prevSnapshots.findIndex(s => s.id === snapshotId);
        if (existingIndex >= 0) {
          const updated = [...prevSnapshots];
          updated[existingIndex] = response;
          return updated;
        }
        return prevSnapshots;
      });

      return response;
    } catch (err: any) {
      // ApiError has message and data properties
      const errorMessage = err.message || err.data?.detail || 'Failed to fetch snapshot details';
      throw new Error(errorMessage);
    }
  }, [workspaceId, get]);

  // Initial load - fetch snapshots with restore summary and all restores
  useEffect(() => {
    if (!enabled) {
      // If disabled, immediately set loading to false to prevent spinner
      setLoading(false);
      return;
    }

    if (workspaceId) {
      fetchSnapshots(true); // Include restore summary
      fetchAllRestores(); // Fetch all restores to populate status line
      checkCooldown();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, enabled]); // Only run on workspaceId or enabled change to avoid infinite loop

  // Auto-polling for active operations (snapshots and restores)
  useEffect(() => {
    // Don't poll if hook is disabled
    if (!enabled) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    const hasActiveOperations =
      snapshots.some(s => s.status === 'PENDING' || s.status === 'IN_PROGRESS') ||
      restores.some(r => r.status === 'PENDING' || r.status === 'IN_PROGRESS');

    if (!hasActiveOperations) {
      // Clear any existing interval
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    // Set up polling interval (every 5 seconds) - fetch all data to keep status line updated
    pollingIntervalRef.current = window.setInterval(() => {
      fetchSnapshots(true, true, true); // Include restore summary, reset=true, isBackgroundPoll=true
      fetchAllRestores(); // Fetch all restores to update status line with completed operations
    }, 5000);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [enabled, snapshots, restores, fetchSnapshots, fetchAllRestores]);

  // Auto-polling for cooldown status while cooldown is active
  useEffect(() => {
    // Don't poll if hook is disabled
    if (!enabled || !workspaceId) {
      if (cooldownPollingIntervalRef.current) {
        clearInterval(cooldownPollingIntervalRef.current);
        cooldownPollingIntervalRef.current = null;
      }
      return;
    }

    // Check if cooldown is active
    const isCooldownActive = cooldownStatus && !cooldownStatus.can_create_snapshot;

    if (!isCooldownActive) {
      // Clear any existing interval
      if (cooldownPollingIntervalRef.current) {
        clearInterval(cooldownPollingIntervalRef.current);
        cooldownPollingIntervalRef.current = null;
      }
      return;
    }

    // Set up polling interval (every 30 seconds) to check cooldown status
    cooldownPollingIntervalRef.current = window.setInterval(() => {
      checkCooldown();
    }, 30000);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (cooldownPollingIntervalRef.current) {
        clearInterval(cooldownPollingIntervalRef.current);
        cooldownPollingIntervalRef.current = null;
      }
    };
  }, [enabled, workspaceId, cooldownStatus, checkCooldown]);

  return {
    snapshots,
    restores,
    loading,
    restoresLoading,
    error,
    restoresError,
    creatingSnapshot,
    cooldownStatus,
    snapshotTotal,
    hasMore,
    loadingMore,
    fetchSnapshots,
    loadMoreSnapshots,
    createSnapshot,
    updateSnapshotMetadata,
    deleteSnapshot,
    restoreSnapshot,
    fetchRestores,
    fetchAllRestores,
    fetchActiveRestores,
    getRestoreDetails,
    getSnapshotById,
    checkCooldown,
  };
};

// Export types for use in components
export type { Snapshot, Restore, CreateSnapshotResponse, RestoreSnapshotResponse, CooldownStatus };
